import {
  AbsenceListDto,
  AbsenceType,
  AppointmentType,
  QueryFactory,
} from '@lmt/shared-types';
import { FewMonthMiniCalendar } from '@lmt/unicorn-shared/app/components/absences/FewMonthMiniCalendar';
import { AbsencePopup } from '@lmt/unicorn-shared/app/components/absences/popup/AbsencePopup';
import {
  addMonths,
  eachDayOfInterval,
  endOfDay,
  endOfYear,
  isWithinInterval,
  startOfDay,
  startOfYear,
} from 'date-fns';
import { bindHover, bindPopper } from 'material-ui-popup-state';
import { usePopupState } from 'material-ui-popup-state/hooks';
import React, { FC, useMemo, useState } from 'react';
import { AppPopper } from '../common/ui-kit/tooltip/AppPopper';
import styles from './AbsencesAndHolidaysPage.module.scss';
import { useEventCallback } from '@mui/material';

interface Props {
  currentDate: Date;
}

export const AbsenceYearCalendar: FC<Props> = React.memo((props) => {
  const { currentDate } = props;

  const absenceQuery =
    QueryFactory.AppointmentEventsQuery.useSearchAppointmentsQuery({
      from: startOfYear(currentDate),
      to: endOfYear(currentDate),
      eventTypes: [AppointmentType.Absence],
    });

  const absences = useMemo(() => {
    return (absenceQuery?.data as AbsenceListDto[]) ?? [];
  }, [absenceQuery.data]);

  const [popupDate, setPopupDate] = useState<Date | null>(null);

  const highlightedDays = useMemo(() => {
    if (!absences?.length) {
      return { holidays: [], resources: [] };
    }
    const holidays = new Set<number>();
    const resourceUnavailabilityDays = new Set<number>();

    absences.forEach((absence) => {
      const start = startOfDay(absence.start);
      const end = startOfDay(absence.end);
      const interval = eachDayOfInterval({ start: start, end: end });
      if (end.getTime() === absence.end.getTime()) {
        // shouldn't add the last day if it's midnight
        interval.pop();
      }

      for (const day of interval) {
        const timeInMillis = day.getTime();
        if (absence.absenceType === AbsenceType.Holiday) {
          holidays.add(timeInMillis);
        } else {
          resourceUnavailabilityDays.add(timeInMillis);
        }
      }
    });
    return {
      holidays: [...holidays],
      resources: [...resourceUnavailabilityDays],
    };
  }, [absences]);

  const dateIntervals = useMemo(() => {
    const startDate = startOfYear(currentDate);
    return [startDate, addMonths(startDate, 6)];
  }, [currentDate]);

  const popupState = usePopupState({
    variant: 'popper',
    popupId: 'absence-year',
  });
  const onMouseEnter = useEventCallback((target, date: Date | null) => {
    setPopupDate(date);
  });
  return (
    <>
      {dateIntervals.map((date, index) => (
        <FewMonthMiniCalendar
          key={index}
          startDate={date}
          monthCount={6}
          absences={highlightedDays}
          handleMouseEnter={onMouseEnter}
          {...bindHover(popupState)}
        />
      ))}
      {!!popupDate && (
        <AppPopper
          {...bindPopper(popupState)}
          delay={500}
          rootClassName={styles.popper}
        >
          <AbsencePopup
            date={popupDate}
            events={filterAbsencesByDate(absences, popupDate)}
            handleClose={popupState.close}
          />
        </AppPopper>
      )}
    </>
  );
});

export function filterAbsencesByDate(absences: AbsenceListDto[], date: Date) {
  return absences.filter(
    (x) =>
      isWithinInterval(date, {
        start: startOfDay(x.start),
        end: endOfDay(x.end),
      }) && x.end.getTime() !== date.getTime(),
  );
}
